import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneTilt, EnvelopeSimple, Warning } from "@phosphor-icons/react";

/**
 * SendEmailDialog — reusable for quotations, sales orders (& invoice/vendor PO).
 * Posts to whatever `endpoint` prop is provided.
 *
 * Props:
 *   • endpoint   e.g. "/quotations/{id}/send-email"
 *   • docNo      e.g. "CEL-2026-0001" — shown in header
 *   • defaultTo  pre-fill To email from parent doc
 *   • docKind    "Quotation" | "Sales Order" — for subject default
 *   • onClose
 *   • onSent
 */
export default function SendEmailDialog({ endpoint, docNo, docKind = "Document", defaultTo = "", onClose, onSent }) {
  const [form, setForm] = useState({
    to_email: defaultTo || "",
    cc: "",
    bcc: "",
    subject: `${docKind} ${docNo} · Celsius HVAC`,
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [configError, setConfigError] = useState("");

  useEffect(() => { setForm((f) => ({ ...f, to_email: defaultTo || f.to_email })); }, [defaultTo]);

  const send = async () => {
    if (!form.to_email || !form.to_email.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    setBusy(true);
    setConfigError("");
    try {
      const payload = {
        to_email: form.to_email.trim(),
        cc: form.cc ? form.cc.split(",").map((s) => s.trim()).filter(Boolean) : [],
        bcc: form.bcc ? form.bcc.split(",").map((s) => s.trim()).filter(Boolean) : [],
        subject: form.subject,
        message: form.message,
      };
      const { data } = await api.post(endpoint, payload);
      toast.success(`Sent to ${data.to || form.to_email}`);
      onSent?.(data);
      onClose?.();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      // Show config error inline (400 = missing SendGrid key)
      if (e.response?.status === 400 && detail.includes("SENDGRID")) {
        setConfigError(detail);
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <EnvelopeSimple size={18} className="text-[#EA580C]" />
            Send {docKind} · {docNo}
          </DialogTitle>
          <DialogDescription>
            The PDF is generated fresh and delivered via SendGrid. The Celsius sender email is BCC'd automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="To *">
            <Input type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} placeholder="client@example.com" data-testid="email-to" autoFocus />
          </Field>
          <Field label="Cc (comma-separated, optional)">
            <Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} placeholder="colleague@celsius.co.in, ..." data-testid="email-cc" />
          </Field>
          <Field label="Bcc (comma-separated, optional)">
            <Input value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })} data-testid="email-bcc" />
          </Field>
          <Field label="Subject">
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" />
          </Field>
          <Field label="Message (optional — a professional default will be sent if blank)">
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="email-message" />
          </Field>

          {configError && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-[11.5px]" data-testid="email-config-error">
              <div className="flex items-center gap-1 font-semibold text-amber-900 mb-1">
                <Warning size={13} /> SendGrid not configured
              </div>
              <div className="text-amber-800 whitespace-pre-line">{configError}</div>
              <div className="text-amber-700 mt-2 text-[10.5px] italic">
                After adding the keys in <code>backend/.env</code>, run <code>sudo supervisorctl restart backend</code>.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="email-send-btn">
            <PaperPlaneTilt size={13} className="mr-1" /> {busy ? "Sending…" : "Send Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
